import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  PENDING_INVITES_EVENT,
  
  readPendingInvites,
  releasePendingInvites,
  removePendingInvite
} from './pendingInvites';
import type {QueuedInvite} from './pendingInvites';
import { fetchMfaStatus, mfaIsDeferred } from '@/features/Auth/gcp/emailMfa';
import { useUserStore } from '@/entities/user/store';
import { PROFILE_STALE_TIME } from '@/shared/config/queryDefaults';

/** The signed-in user's held invitations, live across this tab and others. */
export function usePendingInvites(): {
  invites: Array<QueuedInvite>;
  remove: (email: string) => void;
} {
  const userId = useUserStore((s) => s.user?.id);
  const [invites, setInvites] = useState<Array<QueuedInvite>>(() =>
    readPendingInvites(userId),
  );
  useEffect(() => {
    const sync = () => setInvites(readPendingInvites(userId));
    sync();
    window.addEventListener(PENDING_INVITES_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PENDING_INVITES_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [userId]);
  const remove = useCallback(
    (email: string) => {
      if (userId) removePendingInvite(userId, email);
    },
    [userId],
  );
  return { invites, remove };
}

/**
 * Mounted once in the signed-in shell: when the account is enrolled and
 * invitations are waiting, send them. Enrolment is read from the same
 * `/auth/mfa/status` query the banner uses; a `degraded` answer is ignored
 * because it reports "enrolled" when the provider is unreachable. The server
 * still has the last word: a release it refuses leaves the invitations held.
 */
export function PendingInvitesRelease(): null {
  const userId = useUserStore((s) => s.user?.id);
  const qc = useQueryClient();
  const { invites } = usePendingInvites();
  const waiting = invites.length > 0;
  const { data } = useQuery({
    queryKey: ['auth', 'mfa', 'status'],
    queryFn: fetchMfaStatus,
    staleTime: PROFILE_STALE_TIME,
    retry: false,
    enabled: waiting,
  });
  const enrolled = !!data && data.enrolled && !data.degraded;
  // One release per user per mount. A release takes each invitation out of
  // storage before sending and puts it back when the send fails, so `waiting`
  // flips false then true again during a failed run; without this guard that
  // flip re-ran the effect and a 5xx (or a refusal the status read did not
  // predict) became a request loop. Held invitations are tried again the next
  // time the shell mounts, which is what "kept for next time" means.
  const attemptedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || !waiting || !enrolled) return;
    if (attemptedFor.current === userId) return;
    attemptedFor.current = userId;
    void releasePendingInvites(userId).then((r) => {
      if (r.sent > 0 || r.failed > 0) {
        void qc.invalidateQueries({ queryKey: ['team', 'invitations'] });
        void qc.invalidateQueries({ queryKey: ['team', 'members'] });
      }
      if (r.sent > 0) {
        toast.success(
          `${r.sent} invitation${r.sent === 1 ? '' : 's'} sent now that your account is secured`,
        );
      }
      if (r.failed > 0) {
        toast.error(
          `Couldn’t send ${r.failed} invitation${r.failed === 1 ? '' : 's'}: resend from the Team page.`,
        );
      }
    });
  }, [userId, waiting, enrolled, qc]);

  return null;
}

/** Whether this user would be refused an invitation for lack of a second factor. */
export function useMustSecureAccount(): boolean {
  const { data } = useQuery({
    queryKey: ['auth', 'mfa', 'status'],
    queryFn: fetchMfaStatus,
    staleTime: PROFILE_STALE_TIME,
    retry: false,
  });
  return !!data && !data.enrolled && !data.degraded && mfaIsDeferred(data);
}
